import React from "react";
import { Check, Upload, Loader2 } from "lucide-react";
import { fileUrl } from "../../lib/api";

const inputCls = "w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood transition-colors";

/**
 * Renders a single form field based on its schema definition.
 * Props:
 *  - field: { id, type, label, required, options?, placeholder?, helper?, input_type? }
 *  - value: current value (string|bool|array)
 *  - onChange: (newValue) => void
 *  - showDates: array (for availability_calendar)
 *  - onFileUpload: async (file) => fileId (for file_upload)
 *  - uploading: bool (for file_upload feedback)
 *  - filename: string (display)
 */
export default function FieldRenderer({ field, value, onChange, showDates = [], onFileUpload, uploading = false, filename = "" }) {
  const t = field.type;

  // Non-input static text block
  if (t === "text_block") {
    return (
      <div
        data-testid={`field-${field.id}`}
        className="text-sm leading-relaxed text-[#A19D94] whitespace-pre-wrap"
      >
        {field.content || field.label || ""}
      </div>
    );
  }

  // Common inline label/helper rendering
  const Helper = field.helper ? <p className="text-[10px] text-[#6B6862] mt-1">{field.helper}</p> : null;

  if (t === "short_text" || t === "email" || !["long_text","dropdown","multiple_choice","file_upload","date","number","yes_no","availability_calendar","checkbox","text_block"].includes(t)) {
    return (
      <>
        <input
          data-testid={`field-${field.id}`}
          type={field.input_type || (field.id === "email" ? "email" : "text")}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className={inputCls}
        />
        {Helper}
      </>
    );
  }

  if (t === "long_text") {
    return (
      <>
        <textarea
          data-testid={`field-${field.id}`}
          rows={field.rows || 4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className={`${inputCls} resize-none`}
        />
        {Helper}
      </>
    );
  }

  if (t === "dropdown") {
    return (
      <>
        <select data-testid={`field-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
          <option value="">{field.placeholder || "Select…"}</option>
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {Helper}
      </>
    );
  }

  if (t === "multiple_choice") {
    return (
      <>
        <div className="space-y-2">
          {(field.options || []).map((opt) => (
            <button
              key={opt} type="button" onClick={() => onChange(opt)}
              data-testid={`field-${field.id}-${opt.toLowerCase().replace(/\s+/g, '-').slice(0,40)}`}
              className={`w-full text-left px-4 py-3 border transition-all ${
                value === opt ? "border-blood text-[#F2EBE1] bg-blood/5" : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"
              }`}
            >{opt}</button>
          ))}
        </div>
        {Helper}
      </>
    );
  }

  if (t === "yes_no") {
    return (
      <>
        <div className="flex gap-3">
          {[["Yes", true], ["No", false]].map(([lbl, v]) => (
            <button
              key={lbl} type="button" onClick={() => onChange(v)}
              data-testid={`field-${field.id}-${lbl.toLowerCase()}`}
              className={`flex-1 py-3 border transition-all ${
                value === v ? "border-blood text-blood bg-blood/5" : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"
              }`}
            >{lbl}</button>
          ))}
        </div>
        {Helper}
      </>
    );
  }

  if (t === "checkbox") {
    return (
      <label className="flex items-start gap-3 cursor-pointer">
        <input data-testid={`field-${field.id}`} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-4 h-4 accent-[#D91616]" />
        <span className="text-sm leading-relaxed text-[#A19D94]">{field.checkbox_label || field.label}</span>
      </label>
    );
  }

  if (t === "date") {
    return (
      <>
        <input data-testid={`field-${field.id}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
        {Helper}
      </>
    );
  }

  if (t === "number") {
    return (
      <>
        <input data-testid={`field-${field.id}`} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} placeholder={field.placeholder || ""} className={inputCls} />
        {Helper}
      </>
    );
  }

  if (t === "file_upload") {
    return (
      <>
        <label className="flex items-center gap-3 border border-dashed border-[#3a3835] hover:border-blood p-6 cursor-pointer transition-colors">
          <Upload size={20} className="text-[#A19D94]" />
          <div className="flex-1">
            <div className="text-sm">{filename || field.placeholder || "Click to upload"}</div>
            {value && <div className="text-xs text-blood mt-0.5">✓ Uploaded</div>}
          </div>
          {uploading && <Loader2 size={16} className="animate-spin text-blood" />}
          <input data-testid={`field-${field.id}`} type="file" accept={field.accept || "image/*,application/pdf"} onChange={(e) => e.target.files?.[0] && onFileUpload(e.target.files[0])} className="hidden" />
        </label>
        {value && <img alt="preview" src={fileUrl(value)} className="mt-3 w-24 h-24 object-cover border border-[#262626]" />}
        {Helper}
      </>
    );
  }

  if (t === "availability_calendar") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    if (showDates.length === 0) {
      return <p className="text-sm text-[#6B6862] italic">No open dates have been published yet. You can still submit your application — we&apos;ll collect availability later.</p>;
    }
    const allIds = showDates.map((sd) => sd.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));
    const toggleAll = () => onChange(allSelected ? [] : allIds);
    return (
      <>
        <label className="flex items-center gap-2 mb-3 cursor-pointer select-none" data-testid={`field-${field.id}-select-all`}>
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => { if (el) el.indeterminate = selected.length > 0 && !allSelected; }}
            onChange={toggleAll}
            className="w-4 h-4 accent-[#D91616]"
          />
          <span className="text-sm text-[#F2EBE1]">Select all dates (I'm available for every date)</span>
        </label>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {showDates.map((sd) => {
            const sel = selected.includes(sd.id);
            const date = new Date(sd.date);
            return (
              <button key={sd.id} type="button" onClick={() => toggle(sd.id)} data-testid={`field-${field.id}-${sd.id}`}
                className={`text-left p-3 border transition-all ${sel ? "border-blood bg-blood/5" : "border-[#262626] hover:border-[#3a3835]"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-xs text-[#A19D94]">{`${date.toLocaleDateString('en-GB', { weekday: 'long' })} ${date.getDate()} ${date.toLocaleDateString('en-GB', { month: 'long' })} ${date.getFullYear()}`}</div>
                    <div className="text-sm mt-1 font-medium">{sd.name}</div>
                    <div className="font-mono text-[10px] text-[#6B6862] mt-1">{sd.start_time}–{sd.end_time}</div>
                  </div>
                  {sel && <Check size={16} className="text-blood" />}
                </div>
              </button>
            );
          })}
        </div>
        <div className="text-xs text-[#6B6862] font-mono mt-2">{selected.length} date{selected.length !== 1 ? "s" : ""} selected</div>
        {Helper}
      </>
    );
  }

  return <p className="text-sm text-blood">Unknown field type: {t}</p>;
}
